import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/system';
import { t } from 'i18next';
import {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { GlassMagnifier } from 'react-image-magnifiers';

import { Nullable } from '~types/Nullable';
import { kiloMega } from '~utils/kiloMega';

type fileTypes = ('jpg' | 'jpeg' | 'png') | undefined;

interface Dimensions {
  width: number;
  height: number;
}

export interface ImageUploadHandler {
  reset: () => void;
}

interface ImageUploadProps {
  allowDelete?: boolean;
  disabled?: boolean;
  // Server-side upload failure (e.g. a 422 content-type/processability rejection),
  // set by the parent after `onChange` kicks off the actual upload. Shown inline
  // regardless of whether the widget is displaying the dropzone or the staged
  // file preview, since the preview renders optimistically before the parent's
  // upload call resolves.
  errorMessage?: Nullable<string>;
  imageViewMaxHeight?: string;
  isCanBeMagnified?: boolean;
  maxSize?: number;
  onChange: (file: File) => void;
  removeCallback?: () => void;
  required?: boolean;
  secondaryText?: string;
  sx?: SxProps<Theme>;
  types?: fileTypes[];
  url?: Nullable<string>;
}

const ImageUpload = forwardRef(function LogoUpload(
  {
    maxSize,
    required,
    disabled,
    types,
    url,
    sx,
    onChange,
    removeCallback,
    imageViewMaxHeight,
    isCanBeMagnified = false,
    allowDelete = true,
    secondaryText = t('file_upload.crop_warning'),
    errorMessage,
  }: ImageUploadProps,
  ref: Ref<ImageUploadHandler>,
) {
  const reader = new FileReader();
  const fileTypes = useMemo(() => {
    return types || ['png', 'jpeg', 'jpg'];
  }, [types]);

  const maxFileSize = useMemo(() => {
    return maxSize || 0.1;
  }, [maxSize]);

  const minImageSize = useMemo<Dimensions>(() => ({ width: 125, height: 25 }), []);

  const [file, setFile] = useState<Nullable<File>>(null);
  const [fileURL, setFileURL] = useState<Nullable<string>>(null);
  const [imageSize, setImageSize] = useState<Nullable<Dimensions>>(null);
  const [error, setError] = useState<Nullable<string>>(null);
  // A freshly-triggered client-side validation error (wrong type, too large, below the
  // minimum dimensions) is more relevant to the user than a stale server error from a
  // previous attempt, so it takes precedence when both are present.
  const displayedError = error || errorMessage;

  useImperativeHandle(ref, () => ({
    reset: () => {
      setFile(null);
      setFileURL(null);
      setImageSize(null);
    },
  }));

  const handleChange = (file: File) => {
    if (!disabled) {
      setFile(file);
      onChange(file);
    }
  };

  useEffect(() => {
    setFileURL(url);
    if (url) {
      getImageSizes(url).then((sizes: Dimensions) => {
        setImageSize(sizes);
      });
    }
  }, [url]);

  const onTypeError = () => {
    setError(`${t('file_upload.errors.type')}`);
  };

  const onFileSizeError = () => {
    // Return rootStore.toasterStore.push(alert('Incorrect file size', 'error'));
    setError(t('file_upload.errors.size', { maxSize: maxFileSize + ' MB' }));
  };

  const getImageSizes = (dataURL: string): Promise<Dimensions> =>
    new Promise<Dimensions>((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ height: img.height, width: img.width });
      };
      img.src = dataURL;
    });

  const readFile = (file: File) => {
    reader.onloadend = () => {
      const url = URL.createObjectURL(file);
      setError(null);

      getImageSizes(url).then((sizes: Dimensions) => {
        if (sizes.width < minImageSize.width || sizes.height < minImageSize.height) {
          setError(
            t('file_upload.errors.min_dimensions', {
              width: minImageSize.width,
              height: minImageSize.height,
            }),
          );
        } else {
          setImageSize(sizes);
          setFileURL(url);
        }
      });
    };

    reader.onerror = () => {
      setError(t('file_upload.errors.common'));
    };

    reader.readAsArrayBuffer(file);
  };

  useEffect(() => {
    if (file) {
      readFile(file);
    }
  }, [file]);

  const removeFile = () => {
    setFileURL(null);
    setFile(null);
    // Keep fot now, add handler to handle remove file, without form submission
    removeCallback?.();
  };

  return (
    <Box
      data-test-id="image-upload"
      sx={(theme) => ({
        border: `solid 1px ${theme.palette.divider}`,
        borderRadius: 1.5,
      })}
    >
      {fileURL ? (
        <Card elevation={0} sx={{ width: '100%', ...sx }}>
          <CardHeader
            action={
              allowDelete && (
                <Tooltip title={t('actions.delete')}>
                  <IconButton onClick={removeFile}>
                    <DeleteOutline aria-label={t('actions.delete')} />
                  </IconButton>
                </Tooltip>
              )
            }
            title={`${file?.name || ''}`}
            subheader={file?.size ? kiloMega(file.size, { suffix: 'B', count: 2 }) : ''}
            sx={[
              { '& > *': { maxWidth: 'calc(100% - 2em)' } },
              {
                '& > * > *': {
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              },
            ]}
          />

          {isCanBeMagnified ? (
            <div>
              <Box sx={[{ img: { maxWidth: 'none' } }]}>
                <GlassMagnifier
                  style={{ display: 'flex', justifyContent: 'center' }}
                  imageSrc={fileURL}
                  magnifierSize="50%"
                  square={false}
                />
              </Box>
            </div>
          ) : (
            <CardMedia
              component="img"
              image={fileURL}
              alt={file?.name || ''}
              sx={{
                display: 'flex',
                height: '100%',
                maxWidth: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'auto',
                m: 'auto',
                maxHeight: imageViewMaxHeight || 'auto',
              }}
            />
          )}

          <CardContent>
            {imageSize?.width && (
              <Typography>
                {t('file_upload.image_size')}: {imageSize?.width}
                {'x'}
                {imageSize?.height}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {secondaryText}
            </Typography>
            {displayedError && (
              <Typography variant="body2" sx={{ color: 'error.main', mt: 1 }}>
                {displayedError}
              </Typography>
            )}
          </CardContent>
        </Card>
      ) : (
        <FileUploader
          handleChange={handleChange}
          maxSize={maxFileSize}
          onTypeError={onTypeError}
          onSizeError={onFileSizeError}
          name="file"
          types={fileTypes}
          multiple={false}
          required={required || false}
          disabled={disabled || false}
          hoverTitle={''}
        >
          <Box
            sx={[
              {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                color: displayedError ? 'error.main' : 'text.secondary',
                gap: 1,
              },
              (theme) => ({
                borderRadius: 1,
                bgcolor: theme.semantic.surfaceSunken,
                border: `1px dashed ${theme.semantic.border}`,
                p: 2,
              }),
              ...(Array.isArray(sx) ? sx : [sx]),
            ]}
          >
            <Button variant="contained" color="primary" disabled={disabled}>
              {t('file_upload.upload')}
            </Button>
            <Typography
              variant="body1"
              sx={(theme) => ({
                color: theme.semantic.textStrong,
                fontWeight: 'medium',
              })}
            >
              {t('file_upload.click_add_or_drag')}
            </Typography>
            <Typography variant="caption">
              {fileTypes?.join(', ').toUpperCase()},
              {t('file_upload.max_size', { maxSize: maxFileSize + ' MB' })}
              {/*, Min image size:{minImageSize.width}x{minImageSize.height}*/}
            </Typography>
            {displayedError && <Typography sx={{ mt: 1 }}>{displayedError}</Typography>}
          </Box>
        </FileUploader>
      )}
    </Box>
  );
});

export { ImageUpload };
